import {HotTable, HotTableProps} from "@handsontable/react";
import cn from "classnames";
import Handsontable, {CellRange} from "handsontable";
import {RowObject} from "handsontable/common";
import {Settings as ContextMenuSettings, MenuConfig, Selection} from "handsontable/plugins/contextMenu";
import {DetailedSettings} from "handsontable/plugins/mergeCells";
import {BaseRenderer} from "handsontable/renderers";
import {CellMeta, ColumnSettings} from "handsontable/settings";
import {ReactElement, useMemo, useRef} from "react";
import BarLoader from "../../../components/bar-loader/BarLoader";
import {LICENSES} from "../../../constants/Licenses";
import * as css from "./styles/SimpleHoTWrapper.module.scss";

const ORIGINAL_RENDERER = "SimpleHoTWrapper-originalRenderer";
const RENDERER_WRITTEN = "SimpleHoTWrapper-rendererWritten";
const COMPUTED_SOURCE = "computed";

export type SimpleHoTCellType = string | number | null;
export type SimpleHoTCellObject = Record<string, SimpleHoTCellType | SimpleHoTCellType[]>;

export interface BasicSimpleHoTRow {
    isCompound: false;
    values: (SimpleHoTCellType[] | SimpleHoTCellObject);
    children?: BasicSimpleHoTRow[];
    extraData?: Record<string, unknown>;
    // Only applicable to parent rows (aka rows with children). It is ignored otherwise.
    // undefined means no summary update.
    summaryUpdate?: (change: SimpleHoTChange) => SimpleHoTCellType | undefined;
}

export interface CompoundSimpleHotRow {
    isCompound: true;
    verticalColumnMerges: number[]; // NOTE: If you ever add column merges across a row, you need to fix the logic to filter mergedcell edits at table init.
    values: SimpleHoTCellObject[];
    children?: CompoundSimpleHotRow[];
    extraData?: Record<string, unknown>;
    // Only applicable to parent rows (aka rows with children). It is ignored otherwise.
    // undefined means no summary update.
    summaryUpdate?: (change: SimpleHoTChange) => SimpleHoTCellType | undefined;
}

export type SimpleHoTRow = BasicSimpleHoTRow | CompoundSimpleHotRow;

interface LUTItem {
    row: SimpleHoTRow;
    parentRowIdx?: number;
    parent?: SimpleHoTRow;
    childIndexes?: number[];
    compoundRowValuesIndex?: number;
}

export interface SimpleHotColumnSettings extends Omit<ColumnSettings, "data"> {
    data?: string | number;
}

export interface SimpleHoTChange {
    previousValue: SimpleHoTCellType;
    newValue: SimpleHoTCellType;
    col: number | string;
    row: SimpleHoTRow;
    // This is the row number.
    rowId: number;
    parent?: SimpleHoTRow;
    compoundRowValuesIndex?: number;
}

interface InternalSimpleHoTChange extends SimpleHoTChange {
    parentRowId?: number;
}

export interface SimpleHoTCellStyle {
    isReadOnly?: boolean;
    cssClasses?: string[];
    numericFormat?: string;
}

export interface SimpleHoTSelection {
    row: number;
    rowData: SimpleHoTRow;
    compoundRowValuesIndex?: number;
    parentRowId?: number;
}

export interface ContextMenuItem {
    name: string;
    hidden: (
        selectedRows: Set<number>,
        selectedCols: Set<number>,
        selectedRowData: SimpleHoTSelection[],
    ) => boolean;
    disabled: (
        selectedRows: Set<number>,
        selectedCols: Set<number>,
        selectedRowData: SimpleHoTSelection[],
    ) => boolean;
    callback: (
        selectedRows: Set<number>,
        selectedCols: Set<number>,
        selectedRowData: SimpleHoTSelection[],
    ) => void;
}

export interface SimpleHoTWrapperProps {
    columns: SimpleHotColumnSettings[];
    rows: SimpleHoTRow[];
    nestedRows?: boolean;
    hotProps?: Omit<HotTableProps, "data" | "columns" | "ref">;
    // beforeChange is useful for validating/manipulating data, or excluding invalid user input before it actually gets into the table
    beforeChange?: (changes: SimpleHoTChange[]) => SimpleHoTChange[];
    onChange?: (changes: SimpleHoTChange[]) => SimpleHoTChange[] | void;
    withControls?: boolean;
    dataLoading?: boolean;
    cellStyler?: (row: number, col: number, rowData: SimpleHoTRow, cellValue: SimpleHoTCellType, compoundRowValuesIndex?: number) => SimpleHoTCellStyle;
    contextMenu?: Record<string, ContextMenuItem>;
    customRenderer?: (row: SimpleHoTRow, col: string | number, compoundRowValuesIndex?: number) => BaseRenderer | undefined;
    openSectionsPersistOnRender?: boolean;
}

export function SimpleHoTWrapper(props: SimpleHoTWrapperProps): ReactElement {
    const hotRef = useRef<HotTable>(null);
    const openSections = useRef<Set<number>>(new Set());
    const collectedChanges = useRef<InternalSimpleHoTChange[]>([]);
    const changesDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    function showRows(rows: number[]): void {
        const hot = hotRef.current?.hotInstance;
        if(hot) {
            const plugin = hot.getPlugin("hiddenRows");
            plugin.showRows(rows);
            hot.render();
        }
    }

    function hideRows(rows: number[]): void {
        const hot = hotRef.current?.hotInstance;
        if(hot) {
            const plugin = hot.getPlugin("hiddenRows");
            plugin.hideRows(rows);
            hot.render();
        }
    }

    const columnMap = useMemo(() => {
        if(props.columns.length === 0) {
            return new Map<string, number>();
        }

        const ret = new Map<string, number>();
        for(let i = 0; i < props.columns.length; i++) {
            const colVal = props.columns[i]!;
            if(typeof colVal.data === "string") {
                ret.set(colVal.data, i);
            }
        }
        return ret;
    }, [props.columns]);

    function doParentRowUpdates(parentUpdates: {row: number, col: number | string, value: SimpleHoTCellType}[]): void {
        const hot = hotRef.current?.hotInstance;
        if(hot) {
            const combined: [number, number, SimpleHoTCellType][] = parentUpdates.map(update => {
                if(typeof update.col === "string") {
                    const colIndex = columnMap.get(update.col);
                    if(colIndex === undefined) {
                        return null;
                    }
                    return [update.row, colIndex, update.value];
                }
                return [update.row, update.col, update.value];
            }).filter(update => update !== null) as [number, number, SimpleHoTCellType][];
            hot.batchRender(() => {
                hot.setDataAtCell(combined, COMPUTED_SOURCE);
            });
        }
    }

    function debounceChanges(changes: InternalSimpleHoTChange[], hasParentWithSummaryFunc: boolean): void {
        if(changesDebounce.current) {
            clearTimeout(changesDebounce.current);
            changesDebounce.current = null;
        }
        if(props.onChange || hasParentWithSummaryFunc) {
            collectedChanges.current.push(...changes);
            changesDebounce.current = setTimeout(() => {
                // Do this first to prevent losing a timeout set after this one fires.
                changesDebounce.current = null;

                // Do this to prevent losing edits.
                const localChanges = collectedChanges.current;
                collectedChanges.current = [];

                // Group changes such that any changes to the same column are collapsed into one.
                // Since changes are appended to the array, all we have to do is just overwrite anything
                // earlier in the array with values later in the array.
                const groupedChanges: Record<string, InternalSimpleHoTChange> = {};
                for(const change of localChanges) {
                    const key = `${change.rowId}-${change.col}-${change.compoundRowValuesIndex?.toString() ?? ""}`;
                    let groupedChange = groupedChanges[key];
                    if(!groupedChange) {
                        groupedChange = {
                            previousValue: change.previousValue,
                            newValue: change.newValue,
                            col: change.col,
                            row: change.row,
                            parent: change.parent,
                            compoundRowValuesIndex: change.compoundRowValuesIndex,
                            rowId: change.rowId,
                            parentRowId: change.parentRowId
                        };
                        groupedChanges[key] = groupedChange;
                    } else {
                        groupedChange.newValue = change.newValue;
                    }
                }

                // Handle updating parent row summary values.
                // We do this here to have fewer calculations by absorbing multiple changes of a child row into one.
                const changesArray = Object.values(groupedChanges);
                if(hasParentWithSummaryFunc) {
                    const collectedChanges = new Map<string, {row: number, col: number | string, value: SimpleHoTCellType}>();
                    for(const change of changesArray) {
                        if(change.parent && change.parent.summaryUpdate && change.parentRowId !== undefined) {
                            const key = `${change.parentRowId}-${change.col}`;
                            const parentVal = change.parent.summaryUpdate(change);
                            if(parentVal !== undefined) {
                                const existingVal = collectedChanges.get(key);
                                if(existingVal) {
                                    existingVal.value = parentVal;
                                } else {
                                    collectedChanges.set(key, {
                                        row: change.parentRowId,
                                        col: change.col,
                                        value: parentVal
                                    });
                                }
                            }
                        }
                    }
                    doParentRowUpdates(Array.from(collectedChanges.values()));
                }

                if(props.onChange) {
                    props.onChange(changesArray);
                    hotRef.current?.hotInstance?.render();
                }
            }, 500);
        }
    }


    const createMergedCellRenderer = (mergedCells: DetailedSettings[], original: string | BaseRenderer | null | undefined): BaseRenderer => {
        return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
            if(original !== null && original !== undefined) {
                let renderer: BaseRenderer | undefined;
                if(typeof original === "string") {
                    renderer = Handsontable.renderers.getRenderer(original);
                } else {
                    renderer = original;
                }

                if(renderer) {
                    renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                }
            }

            const mergedCell = mergedCells.find(cell => cell.row === row && cell.col === col);
            if(mergedCell) {
                const {rowspan, colspan} = mergedCell;
                td.rowSpan = rowspan;
                td.colSpan = colspan;
            }
        };
    };

    const hideMergedCellRenderer = (columnsToHide: number[], original: string | BaseRenderer): BaseRenderer => {
        return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
            if(original !== null && original !== undefined) {
                let renderer: BaseRenderer | undefined;
                if(typeof original === "string") {
                    renderer = Handsontable.renderers.getRenderer(original);
                } else {
                    renderer = original;
                }

                if(renderer) {
                    renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                }
            }

            if(columnsToHide.includes(col)) {
                td.style.display = "none";
            } else {
                td.style.display = "";
            }
        };
    };

    const unMergeRenderer = (original: string | BaseRenderer): BaseRenderer => {
        return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
            if(original !== null && original !== undefined) {
                let renderer: BaseRenderer | undefined;
                if(typeof original === "string") {
                    renderer = Handsontable.renderers.getRenderer(original);
                } else {
                    renderer = original;
                }

                if(renderer) {
                    renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                }
            }

            td.style.display = "";
            td.rowSpan = 1;
            td.colSpan = 1;
        };
    };

    const addCustomRenderer = (original: string | BaseRenderer, custom: BaseRenderer): BaseRenderer => {
        return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
            if(original !== null && original !== undefined) {
                let renderer: BaseRenderer | undefined;
                if(typeof original === "string") {
                    renderer = Handsontable.renderers.getRenderer(original);
                } else {
                    renderer = original;
                }

                if(renderer) {
                    renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                }
            }
            custom.apply(this, [hot, td, row, col, prop, value, cellProperties]);
        };
    };

    const addClassesRenderer = (original: string | BaseRenderer, classNames: string[]): BaseRenderer => {
        return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
            if(original !== null && original !== undefined) {
                let renderer: BaseRenderer | undefined;
                if(typeof original === "string") {
                    renderer = Handsontable.renderers.getRenderer(original);
                } else {
                    renderer = original;
                }

                if(renderer) {
                    renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                }
            }

            td.classList.add(...classNames);
        };
    };

    const firstRowRenderer = (original: string | BaseRenderer): BaseRenderer => {
        return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
            if(original !== null && original !== undefined) {
                let renderer: BaseRenderer | undefined;
                if(typeof original === "string") {
                    renderer = Handsontable.renderers.getRenderer(original);
                } else {
                    renderer = original;
                }

                if(renderer) {
                    renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                }
            }

            if(!parentIndexes.has(row) || col !== 0) {
                return;
            }
            const contentWrapper = document.createElement("div");

            const toShift = td.childNodes;
            const textDiv = document.createElement("div");
            for(let i = 0; i < toShift.length; i++) {
                const el = toShift[i];
                if(el) {
                    td.removeChild(el);
                    textDiv.appendChild(el);
                }
            }

            const button = document.createElement("button");
            if(openSections.current.has(row)) {
                button.innerText = "-";
                button.setAttribute("aria-label", `Collapse section ${value}`);
            } else {
                button.innerText = "+";
                button.setAttribute("aria-label", `Expand section ${value}`);
            }
            // stops hot from selecting the cell behind it when clicking the expand/collapse button
            button.addEventListener("mousedown", (evt) => {
                evt.stopPropagation();
            });
            button.addEventListener("click", (evt) => {
                evt.stopPropagation();
                const parentRow = lookupTable[row];
                if(parentRow) {
                    if(openSections.current.has(row)) {
                        openSections.current.delete(row);
                        hideRows(parentRow.childIndexes ?? []);
                    } else {
                        openSections.current.add(row);
                        showRows(parentRow.childIndexes ?? []);
                    }
                }
                // when hiding or showing rows, the table width was not being recalculated if the scrollbar toggled on/off
                // it should be doing it inside the hideRows/showRows methods, but calling render here gives us a
                // little safety net to make sure the bounds are recalculated and the table is updated
                hot.render();
            });

            contentWrapper.appendChild(button);
            contentWrapper.appendChild(textDiv);
            contentWrapper.className = css.simpleHoTExpandable;
            td.appendChild(contentWrapper);
        };
    };

    const [config, lookupTable, parentIndexes, hasMergedCells] = useMemo(() => {
        if (!props.openSectionsPersistOnRender) {
            // Clear open sections on row changes.
            openSections.current.clear();
        }

        const {columns, rows, nestedRows, hotProps} = props;
        const [transformedRows, lookupTable, parentIndexes, childIndexes, mergedCells] = transformRows(rows, nestedRows ?? false);
        const parentIndexSet = new Set(parentIndexes);
        const mergeDetailsByRow = mergedCells.groupBy(d => d.row.toString());
        const toHideCells = new Map<number, number[]>();
        for(const mergedCell of mergedCells) {
            // Right now colspan is always 1, so we don't account for it.
            const {row, col, rowspan} = mergedCell;
            for(let i = 1; i < rowspan; i++) {
                const siblingRow = row + i;
                const colsToHide = toHideCells.get(siblingRow);
                if(!colsToHide) {
                    toHideCells.set(siblingRow, [col]);
                } else {
                    colsToHide.push(col);
                }
            }
        }

        const mergedCellsConfig: HotTableProps = {};
        if(mergedCells.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            mergedCellsConfig.rowHeaders = (((_idx: number) => null) as unknown as (idx: number) => string);
            mergedCellsConfig.rowHeaderWidth = 0;
        }

        let contextMenu: ContextMenuSettings = false;
        if(props.contextMenu) {
            const items: MenuConfig = {};
            for(const [key, item] of Object.entries(props.contextMenu)) {
                items[key] = {
                    name: item.name,
                    hidden() {
                        const selectedCells = this.getSelectedRange();
                        if (selectedCells === undefined || selectedCells.length === 0) {
                            return true;
                        }
                        const builtSelectionData = buildHotSelectionItems(this, selectedCells, lookupTable);
                        return item.hidden(
                            builtSelectionData.selectedRows,
                            builtSelectionData.selectedCols,
                            builtSelectionData.selectedRowData,
                        );
                    },
                    disabled() {
                        const selectedCells = this.getSelectedRange();
                        if (selectedCells === undefined || selectedCells.length === 0) {
                            return true;
                        }
                        const builtSelectionData = buildHotSelectionItems(this, selectedCells, lookupTable);
                        return item.disabled(
                            builtSelectionData.selectedRows,
                            builtSelectionData.selectedCols,
                            builtSelectionData.selectedRowData,
                        );
                    },
                    callback(_key: string, _selection: Selection[]) {
                        const selectedCells = this.getSelectedRange();
                        if (selectedCells === undefined || selectedCells.length === 0) {
                            return;
                        }
                        const builtSelectionData = buildHotSelectionItems(this, selectedCells, lookupTable);
                        item.callback(
                            builtSelectionData.selectedRows,
                            builtSelectionData.selectedCols,
                            builtSelectionData.selectedRowData,
                        );
                    }
                };
            }
            contextMenu = {
                items: items
            };
        }

        let computedHiddenRows = [...childIndexes];
        if (props.openSectionsPersistOnRender === true) {
            const childrenOfOpenSections = new Set<number>();
            const parentRowsToKeepOpen = lookupTable.filter((_x, i) => openSections.current.has(i));
            parentRowsToKeepOpen.map(x => x.childIndexes?.map(y => childrenOfOpenSections.add(y)));
            computedHiddenRows = childIndexes.filter(x => !childrenOfOpenSections.has(x));
        }

        const config: HotTableProps = {
            ...(hotProps ?? {}),
            columns: columns,
            // HoT will try to set the height of oversized rows even if we tell to use default height (which usually gets no height in style).
            // This fucks with virtual columns as the columns that contain the height will be eventually
            // be removed from rendering. This forces them to be rendered no matter what.
            // There is nothing you can do with CSS to fix this :(
            //
            // Details for if we wanted to report a bug to HoT.
            //   This is caused by the 3rdparty/walkontable/src/renderer/table.js (aka TableRenderer)
            //   The renderer has some wonderful lines below this comment "// Fix for multi-line content and for supporting `rowHeights` option."
            //   The lines force set a height that cannot be overridden by any hook.
            //   The problem lies in the fact that "oversized rows" cause a height to be set even you set none.
            //   Thus, some rows always have an explicit height. The problem is the explicit height is only set on the first TD in a row.
            //   Virtual column scrolling means the first TD eventually gets unloaded, and boom.
            //
            //   You can work around this by doing the below, or by using a div in the TD and using "rowHeights" and forcing the height on the
            //   div to be "rowHeights - 1" (gotta take one back for 1px border).
            viewportColumnRenderingOffset: columns.length,
            data: transformedRows,
            rowHeaders: false,
            hiddenRows: {
                copyPasteEnabled: false,
                rows: computedHiddenRows
            }  as unknown as Handsontable.plugins.HiddenRows.Settings,
            contextMenu: contextMenu,
            cells: function(row, col): CellMeta {
                const hot = hotRef.current?.hotInstance;
                // The renderer gets kept after the first call to this function.
                // It's easier for us to kill that and ensure every call is a fresh start.
                // Hooray!
                // NOTE: You must keep this in sync with the setting of RENDERER_WRITTEN
                //       at the bottom of this function.
                const alreadyWritten = this[RENDERER_WRITTEN];
                if(alreadyWritten) {
                    this.renderer = this[ORIGINAL_RENDERER];
                } else {
                    this[ORIGINAL_RENDERER] = this.renderer;
                }

                if(nestedRows && parentIndexSet.has(row)) {
                    let localRenderer = this.renderer;
                    if(localRenderer === undefined) {
                        localRenderer = Handsontable.renderers.TextRenderer;
                    }
                    this.renderer = firstRowRenderer(localRenderer);
                }

                const rowData = lookupTable[row];
                if(rowData) {
                    if(props.cellStyler) {
                        let cellValue = null;
                        if (hot) {
                            cellValue = hot.getDataAtCell(row, col);
                        }
                        const cellStyle = props.cellStyler(row, col, rowData.row, cellValue, rowData.compoundRowValuesIndex);
                        if(cellStyle.isReadOnly) {
                            this.readOnly = true;
                        }
                        if(cellStyle.cssClasses && cellStyle.cssClasses.length > 0) {
                            let localRenderer = this.renderer;
                            if(localRenderer === undefined) {
                                localRenderer = Handsontable.renderers.TextRenderer;
                            }
                            this.renderer = addClassesRenderer(localRenderer, cellStyle.cssClasses);
                        }
                        if(cellStyle.numericFormat) {
                            this.numericFormat = {
                                pattern: cellStyle.numericFormat
                            };
                        }
                    }
                    const customRenderer = props.customRenderer?.(rowData.row, col, rowData.compoundRowValuesIndex);

                    if(customRenderer) {
                        let localRenderer = this.renderer;
                        if(localRenderer === undefined) {
                            localRenderer = Handsontable.renderers.TextRenderer;
                        }
                        this.renderer = addCustomRenderer(localRenderer, customRenderer);
                    }
                }

                // Since the mergedcells plugin is dogshit slow, we get do it ourselves on cell render.
                // What this means? We need to get the renderer and somehow wrap it.
                // Since HoT may re-use the TD elements, we must always have a merge renderer that
                // removes any the merge cell stuff we've done :)
                const rowDetails = mergeDetailsByRow[row.toString()];
                const colsToHide = toHideCells.get(row);
                let localRenderer = this.renderer;
                if(localRenderer === undefined) {
                    localRenderer = Handsontable.renderers.TextRenderer;
                }
                if(rowDetails) {
                    this.renderer = createMergedCellRenderer(rowDetails, localRenderer);
                } else if(colsToHide) {
                    this.renderer = hideMergedCellRenderer(colsToHide, localRenderer);
                } else {
                    this.renderer = unMergeRenderer(localRenderer);
                }

                this[RENDERER_WRITTEN] = true;
                return this;
            },
            beforeChange: (changes, _source) => {
                const source = _source as string;
                if(!changes || changes.length === 0 || source === COMPUTED_SOURCE) return;

                if (props.beforeChange) {
                    const translatedChanges: InternalSimpleHoTChange[] = [];
                    for(const change of changes) {
                        if(!change) continue;
                        const [row, col, prevVal, newVal] = change;
                        const item = lookupTable[row];
                        if(!item) continue;

                        translatedChanges.push({
                            rowId: row,
                            parentRowId: item.parentRowIdx,
                            row: item.row,
                            parent: item.parent,
                            col: col,
                            newValue: newVal as SimpleHoTCellType,
                            previousValue: prevVal as SimpleHoTCellType,
                            compoundRowValuesIndex: item.compoundRowValuesIndex
                        });
                    }

                    // once we get back the updated changes, clear out changes and replace it with what was passed back
                    changes.forEach((_, index) => changes[index] = null);
                    const updatedChanges = props.beforeChange(translatedChanges);
                    for (const updatedChange of updatedChanges) {
                        const lutItem = lookupTable.find(x => x.row === updatedChange.row && x.compoundRowValuesIndex === updatedChange.compoundRowValuesIndex);
                        if (lutItem === undefined) continue;
                        const lutItemRowId = lookupTable.indexOf(lutItem);
                        if(lutItemRowId < 0) continue;
                        changes.push([lutItemRowId, updatedChange.col, updatedChange.previousValue, updatedChange.newValue]);
                    }
                }
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            afterChange: (changes, _source) => {
                const source = _source as string;
                if(!changes || changes.length === 0 || source === COMPUTED_SOURCE) {
                    return;
                }

                const translatedChanges: InternalSimpleHoTChange[] = [];
                let hasParentWithSummary = false;
                for(const change of changes) {
                    const [row, col, prevVal, newVal] = change;
                    const item = lookupTable[row];
                    if(!item) {
                        continue;
                    }

                    hasParentWithSummary = item.parent?.summaryUpdate !== undefined;

                    translatedChanges.push({
                        rowId: row,
                        parentRowId: item.parentRowIdx,
                        row: item.row,
                        parent: item.parent,
                        col: col,
                        newValue: newVal as SimpleHoTCellType,
                        previousValue: prevVal as SimpleHoTCellType,
                        compoundRowValuesIndex: item.compoundRowValuesIndex
                    });
                }

                if(translatedChanges.length > 0) {
                    debounceChanges(translatedChanges, hasParentWithSummary);
                }
            },
            beforeUndoStackChange: (_doneActions, _source) => {
                const source: string = _source as string;

                if (source === COMPUTED_SOURCE) {
                    return false;
                }
            },
            ...mergedCellsConfig
        };
        return [config, lookupTable, new Set(parentIndexes), mergedCells.length > 0];
    }, [props.columns, props.rows, props.nestedRows, props.hotProps, props.contextMenu]);

    const classNames = [css.simpleHoTWrapper];
    if(props.withControls === true) {
        classNames.push(css.withControls);
    }
    if(hasMergedCells) {
        classNames.push(css.hasMergedCells);
    }

    return <div className={cn(...classNames)}>
        {/* the zIndex here is to get above the z-index of 180 we set in our handsontable.vizibly.full.scss */}
        <BarLoader loading={props.dataLoading === true} zIndex={181} />
        {props.rows.length > 0 &&
            <HotTable
                licenseKey={LICENSES.HandsOnTable}
                ref={hotRef}
                {...config}
            />
        }
    </div>;
}

function transformRows(rows: SimpleHoTRow[], nestedRows: boolean): [HotTableProps["data"], LUTItem[], number[], number[], DetailedSettings[]]  {
    const parentIndexes: number[] = [];
    const allChildIndexes: number[] = [];
    const lookupTable: LUTItem[] = [];
    const transformedRows: (unknown[] | RowObject)[] = [];
    const mergedCells: DetailedSettings[] = [];

    for(const row of rows) {
        if(!nestedRows) {
            if(!row.isCompound) {
                transformedRows.push(row.values);
                lookupTable.push({
                    row: row
                });
            } else {
                const startRow = transformedRows.length;
                let i = 0;
                for(const sub of row.values) {
                    transformedRows.push(sub);
                    lookupTable.push({
                        row: row,
                        compoundRowValuesIndex: i
                    });
                    i++;
                }
                const endRow = transformedRows.length;
                for(const col of row.verticalColumnMerges) {
                    mergedCells.push({
                        row: startRow,
                        col: col,
                        rowspan: endRow - startRow,
                        colspan: 1
                    });
                }
            }
        } else {
            if(typeof row.values !== "object") {
                throw new Error("Nested rows require objects for row values.");
            }
            const parentIndex = transformedRows.length;
            parentIndexes.push(parentIndex);
            const parentLut: LUTItem = {
                row: row,
            };
            if(!row.isCompound) {
                transformedRows.push(row.values);
                lookupTable.push(parentLut);
            } else {
                const startRow = transformedRows.length;
                parentLut.compoundRowValuesIndex = 0;
                for(let i = 0; i < row.values.length; i++) {
                    let localLut = parentLut;
                    if(i !== 0) {
                        localLut = {
                            ...parentLut,
                            compoundRowValuesIndex: i
                        };
                    }
                    transformedRows.push(row.values[i]!);
                    lookupTable.push(localLut);
                }
                const endRow = transformedRows.length;
                for(const col of row.verticalColumnMerges) {
                    mergedCells.push({
                        row: startRow,
                        col: col,
                        rowspan: endRow - startRow,
                        colspan: 1
                    });
                }
            }
            if(row.children && row.children.length > 0) {
                // Push the parent once we know children exist.
                let childIdx = transformedRows.length;
                const childIndexes: number[] = [];
                for(let i = 0; i < row.children.length; i++) {
                    const child = row.children[i]!;
                    if(!child.isCompound) {
                        transformedRows.push(child.values);
                        lookupTable.push({
                            row: child,
                            parent: row,
                            parentRowIdx: parentIndex
                        });
                        childIndexes.push(childIdx);
                        allChildIndexes.push(childIdx);
                        childIdx++;
                    } else {
                        const startRow = transformedRows.length;
                        for(let j = 0; j < child.values.length; j++) {
                            const subChild = child.values[j]!;
                            transformedRows.push(subChild);
                            lookupTable.push({
                                row: child,
                                parent: row,
                                parentRowIdx: parentIndex,
                                compoundRowValuesIndex: j
                            });
                            childIndexes.push(childIdx);
                            allChildIndexes.push(childIdx);
                            childIdx++;
                        }
                        const endRow = transformedRows.length;
                        for(const col of child.verticalColumnMerges) {
                            mergedCells.push({
                                row: startRow,
                                col: col,
                                rowspan: endRow - startRow,
                                colspan: 1
                            });
                        }
                    }
                }
                parentLut.childIndexes = childIndexes;
            }
        }
    }
    return [transformedRows, lookupTable, parentIndexes, allChildIndexes, mergedCells];
}

function buildHotSelectionItems(
    hotInstance: Handsontable,
    selection: CellRange[],
    lookupTable: LUTItem[],
): {
    selectedRows: Set<number>,
    selectedCols: Set<number>,
    selectedRowData: SimpleHoTSelection[],
} {
    const selectedRows = new Set<number>();
    const selectedCols = new Set<number>();
    const selectedRowData: SimpleHoTSelection[] = [];
    const hiddenRows = new Set(hotInstance.getPlugin("hiddenRows").getHiddenRows());
    // the reason we have to iterate through selection is to account for someone control/command clicking cells to jump around,
    // instead of just having one contiguous selection range
    for (const selectedRange of selection) {
        const startRow = Math.min(selectedRange.from.row, selectedRange.to.row);
        const endRow = Math.max(selectedRange.from.row, selectedRange.to.row);
        const startCol = Math.min(selectedRange.from.col, selectedRange.to.col);
        const endCol = Math.max(selectedRange.from.col, selectedRange.to.col);

        for (let visualRow = startRow; visualRow <= endRow; visualRow ++) {
            if (!hiddenRows.has(visualRow)) {
                selectedRows.add(visualRow);
                const lutItem = lookupTable[visualRow];
                if (lutItem) {
                    selectedRowData.push({
                        row: visualRow,
                        rowData: lutItem.row,
                        compoundRowValuesIndex: lutItem.compoundRowValuesIndex,
                        parentRowId: lutItem.parentRowIdx,
                    });
                }
            }
        }
        for (let visualCol = startCol; visualCol <= endCol; visualCol++) {
            selectedCols.add(visualCol);
        }
    }
    return { selectedRows, selectedCols, selectedRowData };
}
